import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { Link } from 'react-router-dom';
import { 
  Calendar, MapPin, Plus, ExternalLink, Trash2, Loader2, X, 
  Music, Clock, CheckCircle, Upload, AlertCircle, Maximize2 
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SEO from '../components/SEO';
import LoadingScreen from '../components/LoadingScreen';

// COSTANTI SICUREZZA
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_TITLE_LENGTH = 60;
const MAX_DESC_LENGTH = 1000;
const MAX_LOC_LENGTH = 50;
const ADMIN_EMAIL = 'info@tusciahub.it'; 

const Events = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Filtri
  const [selectedCategory, setSelectedCategory] = useState('Tutti');
  const categories = ['Tutti', 'Nightlife', 'Aperitivo', 'Cultura', 'Sport', 'Ateneo'];

  // Stati Modali
  const [isSellOpen, setIsSellOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileError, setFileError] = useState(false);
  
  const [toast, setToast] = useState(null);

  const [newItem, setNewItem] = useState({ 
    title: '', description: '', date: '', time: '', location: '', 
    price: '', category: 'Nightlife', organizer: '', contact_info: '', 
    image: null, termsAccepted: false 
  });

  useEffect(() => {
    const init = async () => {
        setLoading(true);
        await checkUser();
        await fetchItems();
        setLoading(false);
    };
    init();
  }, []);

  // --- FIX SCROLL ---
  useEffect(() => {
    if (isSellOpen || selectedItem) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => { 
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isSellOpen, selectedItem]);

  const checkUser = async () => { 
      const { data: { user } } = await supabase.auth.getUser(); 
      setUser(user); 
      if (user && user.email === ADMIN_EMAIL) {
          setIsAdmin(true);
      }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchItems = async () => {
    try {
      const { data, error } = await supabase
        .from('event_items')
        .select('*, profiles(email)')
        // .gte('date', new Date().toISOString())  <-- COMMENTATO: ORA VEDI TUTTI GLI EVENTI (ANCHE PASSATI)
        .order('date', { ascending: true });
      
      if (error) throw error;
      setItems(data || []);
    } catch (error) { 
      console.error(error); 
    }
  };

  const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
          showToast("Solo immagini (JPG, PNG).", "error");
          e.target.value = null;
          return;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
          setFileError(true);
          setNewItem({ ...newItem, image: null });
          e.target.value = null;
      } else {
          setFileError(false);
          setNewItem({ ...newItem, image: file });
      }
  };

  const handleAddItem = async (e) => {
    e.preventDefault(); 
    if (!user) return showToast("Devi accedere per pubblicare!", "error");
    if (!newItem.termsAccepted) return showToast("Accetta i termini.", "error");
    if (!newItem.image) return showToast("Carica la locandina!", "error");
    if (fileError) return showToast("File troppo grande.", "error");

    if (newItem.title.length > MAX_TITLE_LENGTH) return showToast("Titolo troppo lungo.", "error");
    if (newItem.description.length > MAX_DESC_LENGTH) return showToast("Descrizione troppo lunga.", "error");
    if (newItem.location.length > MAX_LOC_LENGTH) return showToast("Luogo troppo lungo.", "error");
    
    setIsSubmitting(true);
    
    try {
      const fileExt = newItem.image.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}_event.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('events_images')
        .upload(fileName, newItem.image);
      
      if (uploadError) throw uploadError;
      
      const { data: { publicUrl } } = supabase.storage
        .from('events_images')
        .getPublicUrl(fileName);
      
      const fullDate = new Date(`${newItem.date}T${newItem.time}`);
      
      const { error: dbError } = await supabase.from('event_items').insert([{ 
        title: newItem.title, description: newItem.description, 
        date: fullDate.toISOString(), location: newItem.location, 
        price: parseFloat(newItem.price || 0), category: newItem.category, 
        organizer: newItem.organizer, contact_info: newItem.contact_info, 
        image_url: publicUrl, user_id: user.id 
      }]);

      if (dbError) throw dbError;
      
      showToast("Evento pubblicato!", "success");
      setIsSellOpen(false); 
      setNewItem({ title: '', description: '', date: '', time: '', location: '', price: '', category: 'Nightlife', organizer: '', contact_info: '', image: null, termsAccepted: false });
      setFileError(false);
      await fetchItems();

    } catch (error) { showToast("Errore: " + error.message, "error"); } 
    finally { setIsSubmitting(false); }
  };

  const handleDelete = async (itemId, imageUrl) => { 
      if(!window.confirm("Sicuro di voler eliminare?")) return; 
      
      try { 
          if (imageUrl) {
              const fileUrlParts = imageUrl.split('/events_images/');
              if (fileUrlParts.length > 1) {
                  const filePath = fileUrlParts[1];
                  const { error: storageError } = await supabase.storage
                      .from('events_images') 
                      .remove([filePath]);
                  if (storageError) console.error("Errore cancellazione file:", storageError);
              }
          }

          const { error } = await supabase.from('event_items').delete().eq('id', itemId);
          if (error) throw error;

          setItems(items.filter(i => i.id !== itemId)); 
          showToast("Evento eliminato.", "success");
          if(selectedItem?.id === itemId) setSelectedItem(null); 

      } catch(err) { showToast("Errore cancellazione.", "error"); } 
  };

  const filteredItems = items.filter(item => selectedCategory === 'Tutti' || item.category === selectedCategory);

  const inputStyle = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 text-sm font-medium text-slate-700 placeholder:text-slate-400 focus:bg-white focus:ring-2 focus:ring-pink-200 focus:border-pink-300 outline-none transition-all shadow-sm";

  if (loading) return <LoadingScreen />;

  return (
    <div className="relative min-h-screen bg-slate-100 pt-24 pb-20 px-6 font-sans text-slate-800">
      <SEO title="Eventi" description="Nightlife e cultura." url="/eventi" />
      <div className="absolute top-0 right-[-10%] w-[500px] h-[500px] bg-pink-200/40 rounded-full blur-[100px] mix-blend-multiply pointer-events-none"></div>
      
      {toast && (
        <div className={`fixed bottom-10 left-1/2 -translate-x-1/2 z-[10000] animate-in slide-in-from-bottom-5 fade-in duration-300 px-6 py-4 rounded-xl shadow-2xl flex items-center gap-4 border min-w-[320px] ${toast.type === 'error' ? 'bg-red-900 text-white border-red-800' : 'bg-slate-900 text-white border-slate-800'}`}>
            {toast.type === 'error' ? <AlertCircle className="text-red-400"/> : <CheckCircle className="text-green-400"/>}
            <span className="font-bold text-sm">{toast.message}</span>
        </div>
      )}

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>

      <div className="relative z-10 max-w-7xl mx-auto">
        <PageHeader title="Eventi & Nightlife" subtitle="Scopri cosa succede stasera." breadcrumbs={[{label: 'Eventi'}]} />

        {/* --- BARRA FILTRI --- */}
        <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm mb-10">
            <div className="flex flex-wrap xl:flex-nowrap gap-6 items-center justify-between">
                <div className="flex flex-col md:flex-row gap-6 w-full xl:w-auto flex-grow min-w-0">
                    <div className="flex-1 w-full md:w-auto overflow-x-auto pb-4 custom-scrollbar min-w-0">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 block ml-1">Categoria</label>
                        <div className="flex gap-2">
                            {categories.map(c => (
                                <button key={c} onClick={() => setSelectedCategory(c)} className={`px-5 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition border ${selectedCategory === c ? 'bg-pink-600 text-white border-pink-600 shadow-md' : 'bg-white border-slate-200 text-slate-500 hover:bg-white hover:border-pink-200 hover:text-pink-600'}`}>{c}</button>
                            ))}
                        </div>
                    </div>
                </div>
                <button 
                    onClick={() => user ? setIsSellOpen(true) : showToast("Accedi per pubblicare!", "error")} 
                    className="w-full xl:w-auto bg-pink-600 hover:bg-pink-700 text-white px-8 py-3.5 rounded-xl font-bold shadow-lg shadow-pink-200 flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 active:scale-95 shrink-0 mt-2 xl:mt-0"
                >
                    <Plus size={20}/> Aggiungi Evento
                </button>
            </div>
        </div>

        {/* --- GRIGLIA ITEMS --- */}
        {items.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {items.filter(item => selectedCategory === 'Tutti' || item.category === selectedCategory).map(item => {
                    const date = new Date(item.date);
                    return (
                        <div key={item.id} onClick={() => setSelectedItem(item)} className="group bg-gradient-to-b from-white to-pink-50 border-2 border-pink-100 rounded-[2.5rem] overflow-hidden shadow-xl hover:shadow-2xl hover:border-pink-300 transition hover:-translate-y-2 duration-300 flex flex-col cursor-pointer relative">
                            
                            <div className="h-64 bg-slate-100 relative overflow-hidden">
                                <img src={item.image_url} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" alt={item.title}/>
                                
                                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur rounded-2xl p-3 text-center shadow-lg min-w-[60px]">
                                    <span className="block text-2xl font-black text-slate-900 leading-none">{date.getDate()}</span>
                                    <span className="block text-[10px] font-bold text-pink-600 uppercase tracking-widest">{date.toLocaleDateString('it-IT', { month: 'short' }).toUpperCase()}</span>
                                </div>
                                
                                <span className="absolute top-4 right-4 bg-slate-900/80 backdrop-blur text-white text-[10px] font-bold px-3 py-1.5 rounded-full shadow-sm">{item.category}</span>
                                
                                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <span className="bg-white/90 text-slate-900 px-4 py-2 rounded-full font-bold text-sm shadow-lg flex items-center gap-2 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                                        <Maximize2 size={16}/> Vedi
                                    </span>
                                </div>
                            </div>
                            
                            <div className="p-6 flex flex-col flex-grow">
                                <h3 className="font-black text-slate-900 text-2xl leading-tight mb-2 line-clamp-2 group-hover:text-pink-600 transition-colors">{item.title}</h3>
                                <div className="space-y-2 mb-6">
                                    <p className="flex items-center gap-2 text-sm text-slate-600 font-bold"><MapPin size={16} className="text-pink-500"/> {item.location}</p>
                                    <p className="flex items-center gap-2 text-sm text-slate-600 font-bold"><Clock size={16} className="text-pink-500"/> {date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
                                </div>
                                <p className="text-slate-500 text-sm line-clamp-3 mb-6 flex-grow leading-relaxed">{item.description}</p>
                                
                                <div className="mt-auto flex gap-2 pt-4 border-t border-pink-100">
                                    <span className="text-sm font-bold text-pink-600 group-hover:underline flex items-center gap-1">
                                        <Maximize2 size={16}/> Dettagli
                                    </span>
                                    
                                    {user && (user.id === item.user_id || isAdmin) && (
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); handleDelete(item.id, item.image_url); }} 
                                            className="bg-white text-red-500 p-2.5 rounded-xl hover:bg-red-50 ml-auto transition z-20 border border-red-100 shadow-sm"
                                        >
                                            <Trash2 size={18}/>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        ) : (
            <div className="text-center py-24 bg-white border border-dashed border-slate-200 rounded-[3rem]">
                <div className="bg-pink-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 text-pink-400 animate-pulse"><Music size={32}/></div>
                <h3 className="font-bold text-slate-900 text-lg">Nessun evento</h3>
                <p className="text-slate-400 text-sm mb-6">Non ci sono eventi in programma.</p>
                <button onClick={() => user ? setIsSellOpen(true) : showToast("Accedi prima!", "error")} className="text-pink-600 font-bold bg-white border border-pink-200 px-6 py-2 rounded-full hover:bg-pink-50 transition shadow-sm">Organizza tu!</button>
            </div>
        )}

        {/* --- MODALE DETTAGLIO --- */}
        {selectedItem && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setSelectedItem(null)}></div>
                <div className="relative z-10 bg-white w-full max-w-4xl max-h-[90vh] rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col md:flex-row animate-in zoom-in-95 duration-300">
                    <button onClick={() => setSelectedItem(null)} className="absolute top-4 right-4 z-10 p-2 bg-white/50 backdrop-blur rounded-full hover:bg-white text-slate-800 transition shadow-sm"><X size={24}/></button>
                    
                    <div className="w-full md:w-1/2 h-64 md:h-auto bg-slate-100 relative shrink-0">
                        <img src={selectedItem.image_url} className="w-full h-full object-cover" alt={selectedItem.title}/>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6 pt-20">
                            <span className="bg-white text-slate-900 px-3 py-1 rounded-lg text-xs font-black uppercase tracking-widest">{selectedItem.category}</span>
                        </div>
                    </div>

                    <div className="w-full md:w-1/2 p-8 overflow-y-auto bg-white flex flex-col custom-scrollbar">
                        <div className="flex items-center gap-2 text-slate-400 text-sm font-bold mb-2">
                             Organizzato da {selectedItem.organizer}
                        </div>
                        <h2 className="text-3xl font-black text-slate-900 leading-tight mb-4">{selectedItem.title}</h2>
                        
                        <div className="space-y-3 mb-6">
                            <p className="flex items-center gap-3 text-lg font-bold text-slate-700"><Calendar className="text-pink-500"/> {new Date(selectedItem.date).toLocaleDateString('it-IT', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                            <p className="flex items-center gap-3 text-lg font-bold text-slate-700"><Clock className="text-pink-500"/> {new Date(selectedItem.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
                            <p className="flex items-center gap-3 text-lg font-bold text-slate-700"><MapPin className="text-pink-500"/> {selectedItem.location}</p>
                        </div>

                        <div className="prose prose-slate prose-sm text-slate-600 mb-8 leading-relaxed"><p className="whitespace-pre-wrap">{selectedItem.description}</p></div>
                        
                        <div className="mt-auto space-y-3">
                            {selectedItem.contact_info && (
                                <a 
                                    href={selectedItem.contact_info} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="w-full bg-slate-900 text-white py-4 rounded-xl text-lg font-bold flex items-center justify-center gap-2 transition shadow-xl hover:bg-pink-600 hover:shadow-pink-200 active:scale-95"
                                >
                                    <ExternalLink size={24}/> Maggiori Informazioni
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* --- MODALE UPLOAD (STILE HOUSING + SPAZIO EXTRA) --- */}
        {isSellOpen && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setIsSellOpen(false)}></div>
                <div className="relative z-10 bg-white w-full max-h-[90vh] md:max-w-lg rounded-[2.5rem] shadow-2xl flex flex-col animate-in zoom-in-95 duration-200 p-8 pt-10 border border-white/20"> {/* AUMENTATO PADDING TOP */}
                    
                    <button onClick={() => setIsSellOpen(false)} className="absolute top-6 right-6 p-2 bg-slate-50 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition"><X size={20}/></button>
                    
                    <div className="mb-8 pr-10"> {/* AUMENTATO MARGIN BOTTOM */}
                        <h3 className="text-2xl font-black text-slate-900 mb-2">Nuovo Evento</h3>
                        <p className="text-slate-500 text-sm font-medium">Pubblica un evento o una serata.</p>
                    </div>
                    
                    <div className="overflow-y-auto custom-scrollbar flex-1 -mr-4 pr-4">
                        <form onSubmit={handleAddItem} className="space-y-4 pb-2">
                            <div className="space-y-1">
                                <input type="text" required maxLength={MAX_TITLE_LENGTH} className={inputStyle} placeholder="Titolo Evento" value={newItem.title} onChange={e => setNewItem({...newItem, title: e.target.value})} />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <input type="date" required className={inputStyle} value={newItem.date} onChange={e => setNewItem({...newItem, date: e.target.value})} />
                                <input type="time" required className={inputStyle} value={newItem.time} onChange={e => setNewItem({...newItem, time: e.target.value})} />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                 <input type="text" required maxLength={MAX_LOC_LENGTH} className={inputStyle} placeholder="Luogo" value={newItem.location} onChange={e => setNewItem({...newItem, location: e.target.value})} />
                                 <input type="number" className={inputStyle} placeholder="Prezzo (€)" value={newItem.price} onChange={e => setNewItem({...newItem, price: e.target.value})} />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                 <div className="flex bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                                    <select className="w-full bg-transparent px-3 text-sm font-bold outline-none text-slate-600 cursor-pointer" value={newItem.category} onChange={e => setNewItem({...newItem, category: e.target.value})}>
                                        {categories.filter(c=>c!=='Tutti').map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                 </div>
                                 <input type="text" className={inputStyle} placeholder="Link Biglietti/Info" value={newItem.contact_info} onChange={e => setNewItem({...newItem, contact_info: e.target.value})} />
                            </div>

                            <textarea className={`${inputStyle} h-24 resize-none`} placeholder="Descrizione..." maxLength={MAX_DESC_LENGTH} value={newItem.description} onChange={e => setNewItem({...newItem, description: e.target.value})}></textarea>
                            
                            <div className="relative group">
                                <input type="file" accept="image/*" className="hidden" id="img-upload-event" onChange={handleFileChange} />
                                <label htmlFor="img-upload-event" className={`flex flex-col items-center justify-center h-32 w-full border-2 border-dashed rounded-2xl transition-all group-hover:scale-[1.01] cursor-pointer ${fileError ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50 hover:bg-slate-100 hover:border-pink-300'}`}>
                                    {newItem.image ? (
                                        <div className="flex flex-col items-center text-pink-600 animate-in zoom-in">
                                            <CheckCircle size={32} className="mb-1"/>
                                            <span className="text-xs font-bold">{newItem.image.name}</span>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center text-slate-400 group-hover:text-pink-500 transition-colors">
                                            <div className="bg-white p-3 rounded-full shadow-sm mb-2"><Upload size={20}/></div>
                                            <span className="text-xs font-bold">{fileError ? "File troppo grande" : "Carica Locandina"}</span>
                                        </div>
                                    )}
                                </label>
                            </div>

                            <label className="flex items-center gap-3 p-2 cursor-pointer hover:bg-slate-50 rounded-lg transition">
                                <input type="checkbox" required className="w-4 h-4 rounded text-pink-600 focus:ring-pink-500 cursor-pointer" checked={newItem.termsAccepted} onChange={e => setNewItem({...newItem, termsAccepted: e.target.checked})}/>
                                <span className="text-xs text-slate-500 font-medium">Accetto i <Link to="/terms" className="text-pink-600 font-bold hover:underline" target="_blank">Termini e Condizioni</Link>.</span>
                            </label>

                            <button disabled={isSubmitting} type="submit" className="w-full bg-slate-900 text-white py-4 rounded-xl font-bold text-lg hover:bg-pink-600 transition shadow-xl shadow-slate-200 active:scale-95 flex items-center justify-center gap-2">
                                {isSubmitting ? <Loader2 className="animate-spin"/> : 'Pubblica Evento'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default Events;